import React, { useCallback, useEffect, useRef, useState } from "react";
import ashleyGuide from "../../assets/mission-control/ashley-guide.png";
import ashleyUpdatesVideo from "../../assets/mission-control/ashleyupdates.mp4";
import { useHardwareBackHandler } from "../../navigation/HardwareBackContext";
import { logAshleyBriefMounted } from "../shared/pepBootDebug";
import { onBriefMedia, stopEducationNarration } from "../shared/narration/NarrationAdapter";

export function SkipReleaseTourDialog({ onCancel, onConfirmSkip }) {
  return (
    <div className="pep-overlay" role="dialog" aria-modal="true" aria-labelledby="pep-skip-title">
      <div className="pep-panel">
        <div className="pep-panel__header">
          <p className="pep-panel__eyebrow">Release tour</p>
          <h2 id="pep-skip-title" className="pep-panel__title">
            Skip this update overview?
          </h2>
          <p className="pep-panel__subtitle">
            You can replay the release briefing or open What&apos;s New anytime from
            Help &amp; Learning in Settings.
          </p>
        </div>
        <div className="pep-panel__actions">
          <button type="button" className="pep-btn pep-btn--primary" onClick={onCancel}>
            Continue overview
          </button>
          <button type="button" className="pep-btn pep-btn--ghost" onClick={onConfirmSkip}>
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

export function AshleyReleaseBriefPresenter({
  briefScript,
  briefUi,
  onShowMe,
  onJustTellMe,
  onSkip,
  onContinue,
}) {
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [useStaticAvatar, setUseStaticAvatar] = useState(false);
  const videoRef = useRef(null);
  const allowHardwareBackDismiss = briefUi?.allowHardwareBackDismiss !== false;
  const closingPrompt = briefUi?.closingPrompt || briefScript.closingPrompt;

  useEffect(() => {
    logAshleyBriefMounted({
      title: briefScript?.title,
      briefUiMode: briefUi?.mode,
    });
  }, [briefScript?.title, briefUi?.mode]);

  useEffect(() => {
    if (useStaticAvatar) {
      return undefined;
    }

    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    let cancelled = false;

    const handleMediaFailure = () => {
      if (!cancelled) {
        setUseStaticAvatar(true);
      }
      stopEducationNarration();
    };

    video.addEventListener("error", handleMediaFailure);

    onBriefMedia(briefScript, { videoElement: video }).then((started) => {
      if (!started) {
        handleMediaFailure();
      }
    });

    return () => {
      cancelled = true;
      video.removeEventListener("error", handleMediaFailure);
      stopEducationNarration();
    };
  }, [briefScript, useStaticAvatar]);

  const withNarrationStopped = useCallback((handler) => {
    return () => {
      stopEducationNarration();
      handler?.();
    };
  }, []);

  useHardwareBackHandler(() => {
    if (!allowHardwareBackDismiss) {
      return true;
    }

    if (briefUi?.showSkip) {
      setShowSkipConfirm(true);
      return true;
    }

    return true;
  }, true);

  if (showSkipConfirm && briefUi?.showSkip) {
    return (
      <SkipReleaseTourDialog
        onCancel={() => setShowSkipConfirm(false)}
        onConfirmSkip={withNarrationStopped(() => {
          setShowSkipConfirm(false);
          onSkip();
        })}
      />
    );
  }

  return (
    <div
      className="pep-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pep-brief-title"
    >
      <div className="pep-panel">
        <div className="pep-panel__header">
          <p className="pep-panel__eyebrow">
            {briefUi?.mode === "mandatory" ? "Important update" : "What's new"}
          </p>
          <h2 id="pep-brief-title" className="pep-panel__title">
            {briefScript.title || "Welcome Back"}
          </h2>
          {briefScript.theme ? (
            <p className="pep-panel__subtitle">{briefScript.theme}</p>
          ) : null}
        </div>

        <div className="pep-ashley-brief__avatar-wrap">
          {useStaticAvatar ? (
            <img
              src={ashleyGuide}
              alt="Ashley"
              className="pep-ashley-brief__avatar"
            />
          ) : (
            <video
              ref={videoRef}
              className="pep-ashley-brief__avatar pep-ashley-brief__avatar-video"
              autoPlay
              playsInline
              muted
              preload="auto"
              aria-label="Ashley"
            >
              <source src={ashleyUpdatesVideo} type="video/mp4" />
            </video>
          )}
        </div>

        <div className="pep-ashley-brief__body">
          <p className="pep-ashley-brief__greeting">{briefScript.greeting}</p>
          {briefScript.paragraphs.map((paragraph) => (
            <p key={paragraph} className="pep-ashley-brief__paragraph">
              {paragraph}
            </p>
          ))}
          <p className="pep-ashley-brief__prompt">{closingPrompt}</p>
        </div>

        <div className="pep-panel__actions">
          {briefUi?.showContinue ? (
            <button
              type="button"
              className="pep-btn pep-btn--primary"
              onClick={withNarrationStopped(onContinue)}
            >
              Continue
            </button>
          ) : null}
          {briefUi?.showShowMe ? (
            <button
              type="button"
              className="pep-btn pep-btn--primary"
              onClick={withNarrationStopped(onShowMe)}
            >
              Show Me
            </button>
          ) : null}
          {briefUi?.showJustTellMe ? (
            <button
              type="button"
              className={`pep-btn ${
                briefUi?.showShowMe ? "pep-btn--secondary" : "pep-btn--primary"
              }`}
              onClick={withNarrationStopped(onJustTellMe)}
            >
              Just Tell Me
            </button>
          ) : null}
          {briefUi?.showSkip ? (
            <button
              type="button"
              className="pep-btn pep-btn--ghost"
              onClick={() => setShowSkipConfirm(true)}
            >
              Skip
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
